/**
 * PendingInvitesSection - Issue #907
 *
 * Displays pending club invites with Accept/Decline actions.
 * Shows at the top of Settings page when invites are pending.
 *
 * 3-Layer CSS: Uses only Layer 2 semantic tokens.
 */

import { Mail, Check, X } from 'lucide-react';
import { usePendingInvites, useAcceptInvite, useDeclineInvite } from '../hooks/usePendingInvites';
import { formatTimeAgo } from '../lib/formatters';
import type { PendingInvite } from '../lib/api/invites';

export function PendingInvitesSection() {
  const { data: invites, isLoading } = usePendingInvites();

  // Don't render if no invites or loading
  if (isLoading || !invites || invites.length === 0) {
    return null;
  }

  return (
    <section>
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
        Pending Invites
      </h2>
      <div className="space-y-3">
        {invites.map((invite) => (
          <InviteCard key={invite.code} invite={invite} />
        ))}
      </div>
    </section>
  );
}

function InviteCard({ invite }: { invite: PendingInvite }) {
  const acceptMutation = useAcceptInvite();
  const declineMutation = useDeclineInvite();

  const isProcessing = acceptMutation.isPending || declineMutation.isPending;

  const handleAccept = () => {
    if (!isProcessing) {
      acceptMutation.mutate(invite.code);
    }
  };

  const handleDecline = () => {
    if (!isProcessing) {
      declineMutation.mutate(invite.code);
    }
  };

  // #1341: Use centralized formatTimeAgo from formatters.ts
  const getRelativeTime = formatTimeAgo;

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
      <div className="flex items-start gap-3">
        {/* Club avatar or fallback */}
        <div className="w-12 h-12 rounded-lg bg-oro-surface-alt border border-oro-border flex items-center justify-center flex-shrink-0 overflow-hidden">
          {invite.club.hero_image_url ? (
            <img
              src={invite.club.hero_image_url}
              alt={invite.club.name}
              className="w-full h-full object-cover"
              loading="lazy"
              width={48}
              height={48}
            />
          ) : (
            <Mail className="w-5 h-5 text-accent" />
          )}
        </div>

        {/* Invite details */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-text-primary truncate">
            {invite.club.name}
          </h3>
          <p className="text-sm text-text-muted mt-0.5">
            Invited by {invite.inviter.name}
          </p>
          <p className="text-xs text-text-tertiary mt-1">
            {getRelativeTime(invite.created_at)}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleDecline}
            disabled={isProcessing}
            className="min-w-11 min-h-11 flex items-center justify-center rounded-full border border-oro-border hover:bg-oro-surface-alt transition-colors disabled:opacity-50"
            aria-label="Decline invite"
          >
            <X className="w-4 h-4 text-text-muted" />
          </button>
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="min-w-11 min-h-11 flex items-center justify-center rounded-full bg-accent hover:bg-accent/90 transition-colors disabled:opacity-50"
            aria-label="Accept invite"
          >
            <Check className="w-4 h-4 text-oro-bg" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PendingInvitesSection;
